import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Spinner } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import Newsletter from "@/components/site/Newsletter";
import PlaybookCta from "@/components/site/PlaybookCta";
import AdSlot from "@/components/site/AdSlot";
import AdSense from "@/components/site/AdSense";
import AffiliateStrip from "@/components/site/AffiliateStrip";
import ShareButtons from "@/components/site/ShareButtons";
import TipJar from "@/components/site/TipJar";
import ReadingProgress from "@/components/site/ReadingProgress";
import RelatedPosts from "@/components/site/RelatedPosts";
import { getBlogPost } from "@/lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO, { articleSchema, breadcrumbSchema } from "@/components/site/SEO";

function renderInline(text) {
  const parts = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<a key={`l-${i++}`} href={m[2]} target="_blank" rel="noreferrer sponsored" className="text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary">{m[1]}</a>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

function renderContent(content) {
  const out = [];
  let list = null;
  const flushList = (key) => {
    if (list) {
      out.push(<ul key={key} className="list-disc pl-6 mb-6 space-y-2 text-lg text-foreground/85 leading-relaxed">{list}</ul>);
      list = null;
    }
  };
  content.split("\n").forEach((line, i) => {
    const vidM = line.match(/^::video\(([^)]+)\)$/);
    if (vidM) {
      flushList(`ul-${i}`);
      out.push(
        <div key={i} className="my-8 border border-border" data-testid={`blog-video-${i}`}>
          <video src={vidM[1]} autoPlay muted loop playsInline controls preload="metadata" className="w-full" />
        </div>
      );
      return;
    }
    const imgM = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgM) {
      flushList(`ul-${i}`);
      out.push(
        <figure key={i} className="my-8">
          <img src={imgM[2]} alt={imgM[1]} loading="lazy" className="w-full max-h-[420px] object-cover border border-border" />
          {imgM[1] && <figcaption className="mt-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">{imgM[1]}</figcaption>}
        </figure>
      );
      return;
    }
    if (line.startsWith("## ")) {
      flushList(`ul-${i}`);
      out.push(<h2 key={i} className="font-display text-2xl md:text-3xl mt-10 mb-4">{line.replace("## ", "")}</h2>);
      return;
    }
    if (line.startsWith("- ")) {
      (list ??= []).push(<li key={i}>{renderInline(line.slice(2))}</li>);
      return;
    }
    flushList(`ul-${i}`);
    if (line.trim()) out.push(<p key={i} className="text-lg text-foreground/85 leading-relaxed mb-5">{renderInline(line)}</p>);
  });
  flushList("ul-end");
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { setPost(null); getBlogPost(slug).then(setPost).catch(() => setNotFound(true)); }, [slug]);

  if (notFound) return <div className="pt-40 pb-40 text-center"><p className="font-display text-4xl">Article not found.</p><Link to="/blog" className="text-primary mt-4 inline-block link-underline">Back to Blog</Link></div>;
  if (!post) {
    // Mount SEO immediately with slug-derived values so prerender snapshots get the
    // correct URL + a sensible title even if captured before the post data loads.
    const slugTitle = slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return (
      <div className="pt-24">
        <SEO title={slugTitle} description="Read this article on the Julius Mason blog." path={`/blog/${slug}`} type="article" />
        <Spinner />
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <ReadingProgress />
      <SEO
        title={post.title}
        description={post.excerpt || (post.content || "").slice(0, 155)}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.image ? (post.image.startsWith("http") ? post.image : `https://juliusmason.com${post.image}`) : undefined}
        keywords={(post.tags || []).join(", ")}
        schema={[
          breadcrumbSchema([
            { name: "Accueil", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          articleSchema({
            title: post.title,
            description: post.excerpt,
            image: post.image,
            datePublished: post.date,
            dateModified: post.updated_at || post.date,
            url: `https://juliusmason.com/blog/${post.slug}`,
            author: post.author || "Julius Mason",
          }),
        ]}
      />
      <section className="pt-32 md:pt-40 pb-12 border-b border-border">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">{post.category}</p>
          <h1 className="font-display text-4xl md:text-6xl leading-[0.95]">{post.title}</h1>
          <div className="mt-8 flex items-center gap-4 font-mono text-sm text-muted-foreground">
            <span>{post.author}</span><span>·</span><span>{post.date}</span><span>·</span><span>{post.read_time}</span>
          </div>
          <ShareButtons
            title={post.title}
            summary={post.excerpt || ""}
            url={`https://juliusmason.com/blog/${post.slug}`}
            className="mt-8"
          />
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-4xl px-5 md:px-10 relative">
          {/* Sticky vertical share rail — desktop only. Follows the reader down
              the article so sharing stays one click away. */}
          <aside
            aria-label="Share article"
            data-testid="sticky-share-rail"
            className="hidden lg:block absolute -left-16 top-0 h-full"
          >
            <div className="sticky top-32">
              <ShareButtons
                variant="vertical"
                title={post.title}
                summary={post.excerpt || ""}
                url={`https://juliusmason.com/blog/${post.slug}`}
              />
            </div>
          </aside>

          <Reveal>
            <div className="aspect-[16/9] overflow-hidden border border-border mb-12">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <article className="max-w-3xl">
            <p className="text-xl md:text-2xl leading-relaxed text-foreground mb-10">{post.excerpt}</p>
            {renderContent(post.content || "")}
            <div className="flex flex-wrap gap-2 mt-10">{(post.tags || []).map((t) => <span key={t} className="font-mono text-xs border border-border px-3 py-1.5">#{t}</span>)}</div>
          </article>

          {/* Playbook lead magnet — automatic on every article */}
          <PlaybookCta />

          {/* Contextual course CTA — surface the interactive English course on the language post */}
          {(post.slug === "learning-languages-online" || (post.tags || []).some((t) => /language/i.test(t))) && (
            <div className="mt-14 border border-primary/40 rounded-2xl p-8 bg-gradient-to-br from-primary/10 to-transparent max-w-3xl" data-testid="blog-english-course-cta">
              <p className="overline text-primary mb-3">Ready to practise?</p>
              <h3 className="font-display text-3xl md:text-4xl mb-3">Learn English — French → English, right in your browser.</h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                A free interactive course built by Julius for French speakers: bite-sized lessons, real voices, translation drills, listening practice, and everyday scenes — pack a suitcase, order a coffee, close a meeting. Zero downloads, works offline once loaded, keep your streak going.
              </p>
              <Link to="/english" data-testid="blog-english-course-cta-link"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold hover:bg-primary/90 transition">
                Start the course <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          <div className="mt-14 pt-10 border-t border-border">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Share this article</p>
            <ShareButtons
              title={post.title}
              summary={post.excerpt || ""}
              url={`https://juliusmason.com/blog/${post.slug}`}
            />
          </div>

          <div className="mt-14">
            <TipJar />
          </div>

          <div className="mt-16 border border-border p-8 bg-card">
            <p className="overline text-primary mb-2">Enjoyed this?</p>
            <h3 className="font-display text-2xl mb-5">Get new articles in your inbox</h3>
            <Newsletter source="blog-post" className="max-w-md" />
          </div>

          <div className="mt-14">
            <AffiliateStrip
              variant="row"
              title=""
              subtitle=""
              limit={6}
              tags={post.tags || []}
              category={post.category}
            />
          </div>

          <div className="mt-12"><AdSlot placement="blog" /></div>

          {/* AdSense — in-article auto-format. Renders only when a pub ID is set. */}
          <AdSense slot="0000000000" format="auto" className="mt-6" />

          <div className="mt-16">
            <RelatedPosts post={post} source="blog" />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-5 md:px-10 pb-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary"><ArrowLeft className="w-4 h-4" /> All Articles</Link>
      </div>
    </div>
  );
}
